import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography, Paper, Container } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login but save the location they were trying to access
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin role if required
  if (requireAdmin && user) {
    const isAdmin = user.roles.includes('admin');
    if (!isAdmin) {
      return (
        <Container maxWidth="sm">
          <Box sx={{ py: 8 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom color="error">
                Access Denied
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You do not have permission to access this page. Admin privileges are required.
              </Typography>
            </Paper>
          </Box>
        </Container>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
